import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

export function TotpGate() {
  const { me, pendingTotpEnrolled, verifyTotp } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [enrollUri, setEnrollUri] = useState<string | null>(null);
  const [enrollSecret, setEnrollSecret] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [enrollPassword, setEnrollPassword] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [loading, setLoading] = useState(false);

  async function beginEnrollment(e: React.FormEvent) {
    e.preventDefault();
    if (!enrollPassword) return;
    setErr(null);
    setEnrolling(true);
    try {
      const r = await api<{ secret: string; provisioning_uri: string }>(
        "/api/admin/auth/totp/enroll",
        { method: "POST", json: { current_password: enrollPassword } }
      );
      setEnrollSecret(r.secret);
      setEnrollUri(r.provisioning_uri);
      setEnrollPassword(""); // scrub
    } catch (e: any) {
      setErr(e?.message ?? "enroll failed");
    } finally {
      setEnrolling(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await verifyTotp(code);
      nav("/");
    } catch (e: any) {
      setErr(e?.message ?? "invalid code");
    } finally {
      setLoading(false);
    }
  }

  // Already enrolled: show only the 6-digit code prompt. Read from `me`
  // first; fall back to `pendingTotpEnrolled` (captured from the login
  // response) so the right form renders even if /me 401'd transiently
  // and `me` is null. See AuthContext + audit finding #2 (2026-04-25).
  const isEnrolled = me?.totp_enrolled ?? pendingTotpEnrolled;
  if (isEnrolled) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <form onSubmit={submitCode} className="card w-full max-w-sm">
          <h1 className="text-xl font-semibold mb-4">Two-factor code</h1>
          <label className="block text-xs text-slate-400 mb-1">6-digit code</label>
          <input
            className="input mono text-center tracking-[0.5em] mb-4"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            autoFocus
          />
          {err && (
            <div className="text-rose-400 text-xs mb-3" role="alert">
              {err}
            </div>
          )}
          <button
            className="btn-primary w-full"
            disabled={loading || code.length !== 6}
            type="submit"
          >
            {loading ? "Verifying…" : "Continue"}
          </button>
        </form>
      </div>
    );
  }

  // First-time enrollment: step 1 re-password, step 2 QR + code.
  if (!enrollUri) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <form onSubmit={beginEnrollment} className="card w-full max-w-md">
          <h1 className="text-xl font-semibold mb-2">Set up two-factor</h1>
          <p className="text-sm text-slate-400 mb-4">
            Please re-enter your password to enroll a TOTP device.
          </p>
          <input
            type="password"
            autoComplete="current-password"
            className="input mb-4"
            value={enrollPassword}
            onChange={(e) => setEnrollPassword(e.target.value)}
            required
            autoFocus
          />
          {err && <div className="text-rose-400 text-xs mb-3">{err}</div>}
          <button
            className="btn-primary w-full"
            disabled={enrolling || !enrollPassword}
            type="submit"
          >
            {enrolling ? "Preparing…" : "Continue"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <form onSubmit={submitCode} className="card w-full max-w-md">
        <h1 className="text-xl font-semibold mb-4">Scan with your authenticator</h1>
        <p className="text-sm text-slate-400 mb-4">
          Use Google Authenticator, 1Password, Authy, or Aegis — then enter the 6-digit code.
        </p>
        <div className="bg-white p-3 inline-block rounded-lg mb-3">
          <QRCodeSVG value={enrollUri} size={160} />
        </div>
        <div className="text-xs text-slate-500 mb-4">
          Manual entry secret:{" "}
          {showSecret ? (
            <span className="mono select-all text-slate-300">{enrollSecret}</span>
          ) : (
            <button
              type="button"
              onClick={() => setShowSecret(true)}
              className="text-accent-500 hover:underline"
            >
              show
            </button>
          )}
        </div>
        <label className="block text-xs text-slate-400 mb-1">6-digit code</label>
        <input
          className="input mono text-center tracking-[0.5em] mb-4"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          autoFocus
        />
        {err && (
          <div className="text-rose-400 text-xs mb-3" role="alert">
            {err}
          </div>
        )}
        <button
          className="btn-primary w-full"
          disabled={loading || code.length !== 6}
          type="submit"
        >
          {loading ? "Verifying…" : "Continue"}
        </button>
      </form>
    </div>
  );
}
